import React, { useEffect, useRef, useState } from 'react'
import { Pagination as MUIPagination, PaginationItem, PaginationRenderItemParams } from '@mui/material'
import { useTranslation } from 'react-i18next'

import styled, { useTheme } from 'particles/themes/styled'
import Text from 'particles/Text'
import IconButton from 'particles/Button/IconButton'

interface PaginationProps {
    totalItems: number
    itemPerPage: number
    onPageChange?: (page: number) => void
}

const Pagination = ({ totalItems, itemPerPage, onPageChange }: PaginationProps) => {
    const [t] = useTranslation()
    const theme = useTheme()
    const [page, setPage] = useState(0)
    const [totalPage, setTotalPage] = useState(0)
    const isFirstPageMount = useRef(true)

    useEffect(() => {
        if (totalItems === 0 || itemPerPage === 0) {
            isFirstPageMount.current = true
            setPage(0)
            setTotalPage(0)
            return
        }
        const total = Math.ceil(totalItems / itemPerPage)

        const pageNotDefined = total > 0 && isFirstPageMount.current
        if (pageNotDefined) {
            if (isFirstPageMount) isFirstPageMount.current = false
            setPage(1)
            setTotalPage(total)
            onPageChange?.(1)
        } else {
            setTotalPage(total)

            // change active page if it is last page & the page is not exists
            if (page > total) {
                setPage(total)
                onPageChange?.(total)
            } else {
                onPageChange?.(page)
            }
        }
    }, [totalPage, itemPerPage, totalItems, page])

    const handleChange = (value: number) => {
        if (page === value) return
        setPage(value)
    }

    const getFirstItemCount = () => {
        if (totalPage > 0) {
            const firstItem = (page - 1) * itemPerPage
            return firstItem + 1
        }
        return 0
    }

    const getLastItemCount = () => {
        if (totalPage === 0) return 0
        const isLastPage = page === totalPage
        if (isLastPage) {
            return totalItems
        }
        return itemPerPage * page
    }

    const renderPaginationItem = (item: PaginationRenderItemParams) => {
        // customize space between previous & next button with pagination item
        // using margin to add gap, since they are in the same level in the container
        if (item.type === 'previous') {
            return (
                <IconButton
                    icon="caretLeft"
                    color="neutral"
                    size="xs"
                    form="icon"
                    isDisabled={item.disabled || totalPage <= 1}
                    style={{ marginRight: theme.spacing[200] }}
                    onClick={item.onClick}
                />
            )
        } else if (item.type === 'next') {
            // no margin left if total page is 0
            const marginLeft = totalPage > 0 ? theme.spacing[200] : 0
            return (
                <IconButton
                    icon="caretRight"
                    color="neutral"
                    size="xs"
                    form="icon"
                    isDisabled={item.disabled || totalPage <= 1}
                    style={{ marginLeft }}
                    onClick={item.onClick}
                />
            )
        }

        // change style if page is 3 digit or more
        if (item.type === 'page' && item.page && item.page > 99) {
            return <PaginationItem {...item} className="stretch" />
        }

        return <PaginationItem {...item} />
    }

    return (
        <PaginationContainer>
            <Text scale="bodyS500" color="secondary">
                {t('taskType.stepNumberTitle', {
                    // Todo: Variables are mismatched because the i18n copy is incorrect. Update this once the i18n copy is fixed
                    defaultValue: '{{firstItemCount}} - {{lastItemCount}} of {{itemCount}}',
                    firstItemCount: totalItems,
                    lastItemCount: getFirstItemCount(),
                    itemCount: getLastItemCount(),
                })}
            </Text>
            <StyledPagination
                count={totalPage}
                page={page}
                onChange={(_e, value) => handleChange(value)}
                renderItem={renderPaginationItem}
            />
        </PaginationContainer>
    )
}

export default Pagination

const PaginationContainer = styled.div`
    font-family: inherit;
    height: 54px;
    padding: 0px 16px;
    box-sizing: border-box;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[400]};
    border-top: 0.5px solid;
    border-color: ${({ theme }) => theme.colors.divider.primary};
`

const StyledPagination = styled(MUIPagination)`
    font-family: inherit;
    padding: 0 4px;

    .MuiPaginationItem-circular {
        font-family: inherit;
        color: ${({ theme }) => theme.colors.text.neutralAction};
        margin: 0px;
        padding: 0px;
        user-select: none;
        font-weight: ${({ theme }) => theme.fontWeight.medium};
        font-size: ${({ theme }) => theme.fontSize.s};

        &.MuiPaginationItem-page {
            height: 30px;
            min-width: 30px;
            width: 30px;

            &.stretch {
                padding: 5px 12px;
                width: auto;
            }
        }

        &.MuiPaginationItem-ellipsis:hover {
            background-color: transparent;
        }

        &:hover {
            background-color: ${({ theme }) => theme.colors.surface.neutralActionSubPastelHover};
        }
    }
    .MuiPaginationItem-circular.Mui-selected {
        background-color: ${({ theme }) => theme.colors.surface.action};
        color: ${({ theme }) => theme.colors.text.actionInverse};

        &:hover {
            background-color: ${({ theme }) => theme.colors.surface.action};
        }
    }
`
